import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";  // Importera layout-komponenten
import Login from "./components/Login";
import NoLicense from "./components/NoLicense";
import ErrorBoundary from "./components/ErrorBoundary";
import OnboardingWizard, { hasCompletedOnboarding } from "./components/OnboardingWizard";
import { ToastProvider } from "./contexts/ToastContext";
import sessionService from "./services/SessionService";
import settingsService from "./services/SettingsService";
import userService, { PERMISSION_MODULE_NAMES } from "./services/UserService";
import bwraService from "./services/BWRAService";
import customerRiskService from "./services/CustomerRiskService";
import geoRiskService from "./services/GeoRiskService";
import transactionMonitoringService from "./services/TransactionMonitoringService";
import externalModelService from "./services/ExternalModelService";
import complianceChainService from "./services/ComplianceChainService";
import periodicReportingService from "./services/PeriodicReportingService";
// Wave 27 (demo-29) — Admin-flow dual-write services
import authService from "./services/AuthService";
import apiKeyService from "./services/ApiKeyService";
import scheduledValidationService from "./services/ScheduledValidationService";
import notificationService from "./services/NotificationService";
import dataSourceService from "./services/DataSourceService";
import retentionService from "./services/RetentionService";
import reportTemplateService from "./services/ReportTemplateService";
import configTemplateService from "./services/ConfigTemplateService";
import licenseService, { LICENSABLE_MODULES } from "./services/LicenseService";
import entityService from "./services/EntityService";
import storage from "./services/StorageAdapter";
import { initStorage, CRITICAL_KEYS } from "./services/storageInit";
import { t, initTranslations } from "./useTranslations";
import useAccessibleModules from "./hooks/useAccessibleModules";
import { fetchConfig, auth as authApi, setDefaultTenantResolver } from "./api";

// Shared component CSS imported here so webpack bundles them in the main chunk
// with a deterministic order, avoiding mini-css-extract-plugin ordering warnings
// when these styles are also referenced from lazy-loaded module chunks.
import "./components/SkeletonLoader.css";
import "./components/PageHeader.css";
import "./components/UnifiedModal.css";
import "./components/EmptyState.css";
import "./components/UnifiedCard.css";
import "./styles/HelpTooltip.css";
import "./styles/DataValidation.css";

// Lazy-loaded modules — each module is only fetched when the user navigates to it
const Dashboard = lazy(() => import("./modules/Dashboard/Dashboard"));
const CustomerRisk = lazy(() => import("./modules/CustomerRisk/CustomerRisk"));
const GeoRisk = lazy(() => import("./modules/GeoRisk/GeoRisk"));
const Admin = lazy(() => import("./modules/Admin/Admin"));
const BWRA = lazy(() => import("./modules/BWRA/BWRA"));
const TransactionMonitoring = lazy(() => import("./modules/TransactionMonitoring/TransactionMonitoring"));
const PeriodicReporting = lazy(() => import("./modules/PeriodicReporting/PeriodicReporting"));

// Select backend (Supabase or localStorage) based on env vars — must run
// before any service reads so that all subsequent calls use the right backend.
initStorage();

// Auto-tenant för alla /api/*-requests: läs aktiv entity-id från entityService.
// Används tills Keycloak-user har tenant-attribute-mapping (se deploy/README.md).
// Endpoint-anropare kan fortfarande skicka explicit opts.tenantId för att
// overrida per request.
setDefaultTenantResolver(() => entityService.getActiveEntityId());

const applyTheme = (theme) => {
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

const MODULE_COMPONENTS = {
  'dashboard': Dashboard,
  'customer-risk': CustomerRisk,
  'geographical-risk': GeoRisk,
  'bwra': BWRA,
  'transaction-monitoring': TransactionMonitoring,
  'periodic-reporting': PeriodicReporting,
  'admin': Admin
};

// Inner component that can use hooks (must be inside Router context for useNavigate)
const AppRoutes = ({ isAuth, onLogin, onLogout }) => {
  const [isSidebarExpanded, setSidebarExpanded] = useState(false);
  const accessibleIds = useAccessibleModules();
  const allModules = settingsService.getEnabledModules();
  // Modules accessible to the current user and licensed for the active entity
  const enabledModules = allModules.filter(m => accessibleIds.includes(m.id));

  const defaultRoute = enabledModules.length > 0 ? enabledModules[0].route : '/dashboard';

  const moduleFallback = (
    <div className="app-hydrating">
      <span className="app-hydrating-spinner" />
      <span className="app-hydrating-text">{t('app.loading')}</span>
    </div>
  );

  if (!isAuth) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={onLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Suspense fallback={moduleFallback}>
      <Routes>
        <Route path="/login" element={<Navigate to={defaultRoute} replace />} />
        <Route element={<Layout isSidebarExpanded={isSidebarExpanded} toggleSidebar={() => setSidebarExpanded(!isSidebarExpanded)} onLogout={onLogout} />}>
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          {enabledModules.map(mod => {
            const Component = MODULE_COMPONENTS[mod.id];
            if (!Component) return null;
            return <Route key={mod.id} path={mod.route} element={<Component />} />;
          })}
          {/* Fallback: show NoLicense for any module route that exists in settings but isn't accessible */}
          {allModules.filter(m => !accessibleIds.includes(m.id)).map(mod => (
            <Route key={`nolic-${mod.id}`} path={mod.route} element={<NoLicense />} />
          ))}
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

const App = () => {
  // Start as false — auth state is determined after hydration completes.
  // This prevents a flash of unauthenticated UI while Supabase data loads.
  const [isAuth, setIsAuth] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleLogin = useCallback(() => {
    setIsAuth(true);
    // Notify useAccessibleModules to recalculate now that a session exists
    window.dispatchEvent(new Event('rms_licenses_changed'));
    if (!hasCompletedOnboarding()) {
      setShowOnboarding(true);
    }
  }, []);

  const handleLogout = useCallback(() => {
    // Backend-logout (fire-and-forget). Rensar rms_session HttpOnly-cookie
    // så att nästa page-load inte auto-hydrerar backend-sessionen igen.
    // Om backend ej nåbar: tyst ignorera.
    authApi.logout().catch(() => { /* no-op */ });
    storage.clearCache();
    setIsAuth(false);
  }, []);

  useEffect(() => {
    // Async init: hydrate critical keys, refresh session, apply theme.
    // Hydration must complete before the first render so that sync cache
    // reads in services return fresh data (required when SupabaseBackend is
    // active, since its sync getItem() always returns null).
    const init = async () => {
      await storage.hydrate(CRITICAL_KEYS);
      // Hydrate ALL remaining keys from the backend (entity-specific data:
      // data sources, field mappings, model config, indexes, etc.).
      // Required for SupabaseBackend where sync getItem() returns null —
      // without this, entity data appears empty after login.
      await storage.hydrateAll();
      // Migrate legacy session format (localStorage only — no-op for Supabase)
      sessionService.migrateOldSession();
      await sessionService.refreshSession();

      // ------- Auth-hydrering (demo-09-logik, ORÖRD) -------
      // Kritiskt: denna block är IDENTISK med demo-09 som bevisligen fungerade.
      // createSession är det ENDA som behöver köra klart innan setIsAuth.
      // JIT (user-provisioning, licenser) flyttas till en oberoende IIFE
      // längre ner så fel där aldrig kan påverka auth-hydreringen.
      try {
        const backendUser = await authApi.getCurrentUser();
        if (backendUser && backendUser.email && !sessionService.isValidSession()) {
          await sessionService.createSession(backendUser.email, 'backend_oidc');
        }
      } catch (_err) {
        // Ingen backend / ej inloggad mot backend — använd lokal session
      }

      // Determine auth state from hydrated cache (backend- eller lokal-session)
      const isLoggedIn = sessionService.isValidSession();
      setIsAuth(isLoggedIn);
      // Apply saved theme from hydrated cache
      const settings = settingsService.getSettings();
      applyTheme(settings.ui?.theme);

      // Fetch runtime-config från backend (fire-and-forget — vi vill inte
      // blockera login om backend är nere). Cachas via src/api/config.js så
      // useBackendConfig-hook:en kan läsa sync i Login-komponenten.
      fetchConfig().catch(() => { /* backend ej nåbar — Login visar bara lokalt login-formulär */ });

      // INFRA-172 fix (demo-23): backend-preferences MÅSTE syncas INNAN
      // initTranslations() läser cachen, annars hamnar inkognito-login på
      // default-språk istället för användarens senaste val.
      // Race med 1.5s-timeout så UI inte blockas om backend är slow/nere —
      // localStorage-fallback används då (samma beteende som tidigare).
      if (isLoggedIn) {
        try {
          const { ensurePreferencesSync } = await import('./hooks/usePreferences');
          await Promise.race([
            ensurePreferencesSync({ force: true }),
            new Promise((resolve) => setTimeout(resolve, 1500)),
          ]);
        } catch (_) { /* fallback till localStorage */ }
      }
      // Nu har preferences-cachen rätt värden för inloggad user (eller är
      // oförändrad om backend ej nåbar / ej inloggad). initTranslations()
      // kan läsa språk korrekt.
      initTranslations();
      setIsHydrating(false);

      // JIT-provisionering som HELT OBEROENDE bonus-steg. Auth är redan
      // satt ovan; detta block kan kasta utan att påverka isAuth eller
      // render. Körs som fire-and-forget IIFE så att väntan på addUser +
      // licens-loop inte blockar setIsHydrating(false) ovan.
      //
      // Vid fel lagras info i window.__rmsDebug för dev-tools-inspektion.
      // När JIT skriver till userService triggas subscribe-listeners
      // (useAccessibleModules mfl.) så UI re-rendrerar automatiskt när
      // data finns.
      if (sessionService.isValidSession()) {
        (async () => {
          try {
            const backendUser = await authApi.getCurrentUser();
            if (!backendUser || !backendUser.email) return;
            const role = backendUser.role || 'viewer';
            const nameParts = (backendUser.name || '').split(' ');

            // Module-access: admin får alla moduler, andra roller får dashboard
            // som minimum. Utan explicit modules-lista filtrerar
            // useAccessibleModules-hooken bort ALLA moduler vid tom array
            // (modules=[] → tom allowedIds → sidebar blank). demo-12 missade
            // detta — admin hade modules=[] → tom sidbar trots role=admin.
            const defaultModules = role === 'admin'
              ? [...PERMISSION_MODULE_NAMES]
              : ['Dashboard', 'Admin'];

            // Steg 1: säkerställ att user finns med rätt role + modules
            const existing = userService.getUsers().find(u => u.email === backendUser.email);
            if (!existing) {
              await userService.addUser({
                email: backendUser.email,
                firstName: nameParts[0] || backendUser.email.split('@')[0],
                lastName: nameParts.slice(1).join(' ') || '',
                role,
                modules: defaultModules,
                source: 'keycloak_oidc',
              });
            } else {
              const patch = {};
              if (existing.role !== role) patch.role = role;
              if (existing.source !== 'keycloak_oidc') patch.source = 'keycloak_oidc';
              // Om modules saknas eller är tom array → sätt till defaultModules.
              // Rör inte om user redan har en lista (admin kan ha begränsat själv).
              if (!Array.isArray(existing.modules) || existing.modules.length === 0) {
                patch.modules = defaultModules;
              }
              if (Object.keys(patch).length > 0) {
                await userService.updateUser(existing.id, patch);
              }
            }

            // Steg 2: två-stegs-approach för status (undviker race-condition
            // som demo-10 hade när status:active skickades direkt till addUser).
            const created = userService.getUsers().find(u => u.email === backendUser.email);
            if (created && created.status !== 'active') {
              await userService.updateUser(created.id, { status: 'active' });
            }

            // Steg 2.5 (ADM-160): pulla entiteter från backend INNAN steg 3,
            // så licens-loopen ser samma entity-set som finns i rms_entities.
            // Utan detta skulle fresh device skapa "Default Organization" lokalt
            // även om backend redan har entiteter från andra devices.
            try {
              await entityService.initFromBackend?.();
            } catch (err) {
              if (typeof window !== 'undefined') {
                window.__rmsDebug = { ...(window.__rmsDebug || {}), entityInit: { message: err?.message } };
              }
            }

            // Steg 3: säkerställ minst en entity finns. På fresh Supabase-state
            // är rms_entities tom efter hydrateAll (EntityService._ensureData
            // skrivning hinner inte nå DB innan hydrate läser tillbaka den).
            // Utan entity → licens-loopen nedan loopar 0 ggr → No License.
            let entities = entityService.getEntities() || [];
            if (entities.length === 0) {
              try {
                await entityService.addEntity({
                  name: 'Default Organization',
                  type: 'parent',
                  parentId: null,
                });
                entities = entityService.getEntities() || [];
              } catch (err) {
                if (typeof window !== 'undefined') {
                  window.__rmsDebug = { ...(window.__rmsDebug || {}), jitDefaultEntity: { message: err?.message } };
                }
              }
            }

            // Steg 4: aktivera licenser per entity × module
            for (const entity of entities) {
              if (!entity || !entity.id) continue;
              for (const moduleId of LICENSABLE_MODULES) {
                if (!licenseService.isLicensed(entity.id, moduleId)) {
                  await licenseService.activateLicense(entity.id, moduleId);
                }
              }
            }

            // INFRA-112: sync från backend (users, settings, user-preferences).
            // Efter login pullas auktoritativ state från backend så localStorage
            // speglar DB. Dual-write i services håller backend synkad framåt.
            try {
              const { ensurePreferencesSync } = await import('./hooks/usePreferences');
              await Promise.all([
                userService.initFromBackend?.().catch(() => {}),
                settingsService.initFromBackend?.().catch(() => {}),
                bwraService.initFromBackend?.().catch(() => {}),
                customerRiskService.initFromBackend?.().catch(() => {}),
                geoRiskService.initFromBackend?.().catch(() => {}),
                transactionMonitoringService.initFromBackend?.().catch(() => {}),
                externalModelService.initFromBackend?.().catch(() => {}),
                complianceChainService.initFromBackend?.().catch(() => {}),
                periodicReportingService.initFromBackend?.().catch(() => {}),
                // Wave 27 (demo-29) — Admin-flow services
                authService.initFromBackend?.().catch(() => {}),
                apiKeyService.initFromBackend?.().catch(() => {}),
                scheduledValidationService.initFromBackend?.().catch(() => {}),
                notificationService.initFromBackend?.().catch(() => {}),
                dataSourceService.initFromBackend?.().catch(() => {}),
                retentionService.initFromBackend?.().catch(() => {}),
                reportTemplateService.initFromBackend?.().catch(() => {}),
                configTemplateService.initFromBackend?.().catch(() => {}),
                ensurePreferencesSync({ force: true }).catch(() => {}),
              ]);
            } catch (err) {
              if (typeof window !== 'undefined') {
                window.__rmsDebug = { ...(window.__rmsDebug || {}), backendSync: { message: err?.message } };
              }
            }

            // Signalera att moduler/licenser potentiellt ändrats så
            // useAccessibleModules re-renderar med färska värden.
            window.dispatchEvent(new Event('rms_licenses_changed'));
            window.dispatchEvent(new Event('rms_modules_changed'));
          } catch (err) {
            if (typeof window !== 'undefined') {
              window.__rmsDebug = { ...(window.__rmsDebug || {}), jit: { message: err?.message, stack: err?.stack } };
            }
          }
        })();
      }
    };
    init();

    // Event listeners set up synchronously — do not wait for hydration
    const handleThemeChange = (e) => applyTheme(e.detail.theme);
    window.addEventListener('rms_theme_changed', handleThemeChange);

    const handleSessionExpired = () => setIsAuth(false);
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('rms_theme_changed', handleThemeChange);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  if (isHydrating) {
    return (
      <div className="app-hydrating">
        <span className="app-hydrating-spinner" />
        <span className="app-hydrating-text">{t('app.loading')}</span>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AppRoutes isAuth={isAuth} onLogin={handleLogin} onLogout={handleLogout} />
          {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
